// Editor context providing title, content, auto-save, and history management.

import {
  createContext,
  useContext,
  createSignal,
  createEffect,
  onCleanup,
  type ParentComponent,
  type Accessor,
} from 'solid-js';
import { useAuth } from './AuthContext';
import { useEventSource } from './EventSourceContext';
import { useWorkspace } from './WorkspaceContext';
import { useI18n } from '../i18n';
import { debounce } from '../utils/debounce';
import { nodeUrl } from '../utils/urls';
import { extractLinkedNodeIds, relativeLinksToSpaUrls, spaUrlsToRelativeLinks } from '../utils/markdown-utils';
import { EventNodeUpdated, type Commit } from '@sdk/types.gen';

/** Map of asset filename to signed URL */
export type AssetUrlMap = Record<string, string>;

/** Map of node ID to title for resolving internal page links */
export type NodeTitleMap = Record<string, string>;

interface EditorContextValue {
  // Editor state
  title: Accessor<string>;
  setTitle: (title: string) => void;
  content: Accessor<string>;
  setContent: (content: string) => void;
  hasUnsavedChanges: Accessor<boolean>;
  setHasUnsavedChanges: (has: boolean) => void;
  autoSaveStatus: Accessor<'idle' | 'saving' | 'saved'>;

  // Asset URLs (filename -> signed URL)
  assetUrls: Accessor<AssetUrlMap>;

  // Linked node titles (for resolving page link display text)
  linkedNodeTitles: Accessor<NodeTitleMap>;

  // History
  showHistory: Accessor<boolean>;
  setShowHistory: (show: boolean) => void;
  history: Accessor<Commit[]>;
  loadHistory: (nodeId: string) => Promise<void>;

  // External change detection (SSE)
  externalChange: Accessor<boolean>;
  dismissExternalChange: () => void;
  refreshFromServer: () => void;

  // Frontmatter (icon and cover)
  icon: Accessor<string>;
  cover: Accessor<string>;
  handleIconChange: (newIcon: string) => Promise<void>;
  handleCoverChange: (newCover: string) => Promise<void>;

  // Actions
  triggerAutoSave: () => void;
  flushAutoSave: () => void;
  resetEditor: () => void;
  handleTitleChange: (newTitle: string) => void;
  handleContentChange: (newContent: string) => void;
}

const EditorContext = createContext<EditorContextValue>();

export const EditorProvider: ParentComponent = (props) => {
  const { t } = useI18n();
  const { user, wsApi } = useAuth();
  const { selectedNodeId, selectedNodeData, updateNodeTitle, updateNodeIcon, loadNode, setLoadError, setSaveError } =
    useWorkspace();
  const { lastEvent } = useEventSource();

  // Editor state
  const [title, setTitle] = createSignal('');
  const [content, setContent] = createSignal('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = createSignal(false);
  const [autoSaveStatus, setAutoSaveStatus] = createSignal<'idle' | 'saving' | 'saved'>('idle');

  // Frontmatter state
  const [icon, setIcon] = createSignal('');
  const [cover, setCover] = createSignal('');

  // Asset URLs accessor - derived from selectedNodeData
  const assetUrls = (): AssetUrlMap => selectedNodeData()?.asset_urls || {};

  // Linked node titles for resolving page link display text
  const [linkedNodeTitles, setLinkedNodeTitles] = createSignal<NodeTitleMap>({});

  // History state
  const [showHistory, setShowHistory] = createSignal(false);
  const [history, setHistory] = createSignal<Commit[]>([]);

  // External change detection (SSE)
  const [externalChange, setExternalChange] = createSignal(false);
  const dismissExternalChange = () => setExternalChange(false);
  const refreshFromServer = () => {
    const nodeId = selectedNodeId();
    if (nodeId) {
      setExternalChange(false);
      loadNode(nodeId);
    }
  };

  // Detect external edits on current page via SSE
  createEffect(() => {
    const evt = lastEvent();
    if (!evt) return;
    if (evt.type === EventNodeUpdated && evt.node_id === selectedNodeId()) {
      setExternalChange(true);
    }
  });

  // Debounced auto-save
  // eslint-disable-next-line solid/reactivity -- intentionally reads current signal values when debounced function executes
  const debouncedAutoSave = debounce(async () => {
    const nodeId = selectedNodeId();
    const ws = wsApi();
    if (!nodeId || !hasUnsavedChanges() || !ws) return;

    try {
      setAutoSaveStatus('saving');
      const wsId = user()?.workspace_id || '';
      const diskContent = spaUrlsToRelativeLinks(content(), wsId);
      await ws.nodes.page.updatePage(nodeId, { title: title(), content: diskContent });
      setHasUnsavedChanges(false);
      setAutoSaveStatus('saved');

      // Update URL if title changed
      const wsName = user()?.workspace_name;
      if (wsId) {
        const currentPath = window.location.pathname;
        const newPath = nodeUrl(wsId, wsName, nodeId, title());
        if (currentPath !== newPath) {
          window.history.replaceState(null, '', newPath);
        }
      }

      setTimeout(() => {
        if (autoSaveStatus() === 'saved') {
          setAutoSaveStatus('idle');
        }
      }, 2000);
    } catch (err) {
      setSaveError(`${t('errors.autoSaveFailed')}: ${err}`);
      setAutoSaveStatus('idle');
    }
  }, 2000);

  // Cleanup debounce on unmount
  onCleanup(() => {
    debouncedAutoSave.flush();
  });

  // Fetch linked node titles when content has internal links
  async function fetchLinkedNodeTitles(nodeContent: string) {
    const ws = wsApi();
    const wsId = user()?.workspace_id;
    if (!ws || !wsId || !nodeContent) {
      setLinkedNodeTitles({});
      return;
    }

    // Extract node IDs from internal links
    const linkedIds = extractLinkedNodeIds(nodeContent, wsId);
    if (linkedIds.length === 0) {
      setLinkedNodeTitles({});
      return;
    }

    try {
      const resp = await ws.nodes.getNodeTitles({ IDs: linkedIds.join(',') });
      setLinkedNodeTitles(resp.titles || {});
    } catch {
      // Silent failure - just use stored titles in links
      setLinkedNodeTitles({});
    }
  }

  // Sync editor state when selected node changes
  createEffect(() => {
    const node = selectedNodeData();
    if (node) {
      setTitle(node.title);
      // Convert relative file path links from API to SPA URLs for the editor.
      const wsId = user()?.workspace_id || '';
      const spaContent = relativeLinksToSpaUrls(node.content || '', wsId);
      setContent(spaContent);
      setIcon(node.icon || '');
      setCover(node.cover || '');
      setHasUnsavedChanges(false);
      setAutoSaveStatus('idle');
      setShowHistory(false);
      setExternalChange(false);
      // Fetch linked node titles in the background
      fetchLinkedNodeTitles(spaContent);
    } else {
      resetEditor();
    }
  });

  function resetEditor() {
    setTitle('');
    setContent('');
    setIcon('');
    setCover('');
    setHasUnsavedChanges(false);
    setAutoSaveStatus('idle');
    setShowHistory(false);
    setHistory([]);
    setLinkedNodeTitles({});
    setExternalChange(false);
  }

  async function handleIconChange(newIcon: string) {
    const nodeId = selectedNodeId();
    const ws = wsApi();
    if (!nodeId || !ws) return;
    setIcon(newIcon);
    updateNodeIcon(nodeId, newIcon);
    try {
      await ws.nodes.page.updatePageFrontmatter(nodeId, { icon: newIcon, cover: cover() });
    } catch (err) {
      setSaveError(`${t('errors.failedToSave')}: ${err}`);
    }
  }

  async function handleCoverChange(newCover: string) {
    const nodeId = selectedNodeId();
    const ws = wsApi();
    if (!nodeId || !ws) return;
    setCover(newCover);
    try {
      await ws.nodes.page.updatePageFrontmatter(nodeId, { icon: icon(), cover: newCover });
    } catch (err) {
      setSaveError(`${t('errors.failedToSave')}: ${err}`);
    }
  }

  function handleTitleChange(newTitle: string) {
    setTitle(newTitle);
    const id = selectedNodeId();
    if (id) {
      updateNodeTitle(id, newTitle);
    }
    setHasUnsavedChanges(true);
    debouncedAutoSave();
  }

  function handleContentChange(newContent: string) {
    setContent(newContent);
    setHasUnsavedChanges(true);
    debouncedAutoSave();
  }

  // Local loading state for history - can be exposed later for UI feedback
  const [_loadingHistory, setLoadingHistory] = createSignal(false);

  async function loadHistory(nodeId: string) {
    if (showHistory()) {
      setShowHistory(false);
      return;
    }
    const ws = wsApi();
    if (!ws) return;

    try {
      setLoadingHistory(true);
      const data = await ws.nodes.history.listNodeVersions(nodeId, { Limit: 100 });
      setHistory((data.history?.filter(Boolean) as Commit[]) || []);
      setShowHistory(true);
    } catch (err) {
      setLoadError(`${t('errors.failedToLoad')}: ${err}`);
    } finally {
      setLoadingHistory(false);
    }
  }

  const value: EditorContextValue = {
    title,
    setTitle,
    content,
    setContent,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    autoSaveStatus,
    assetUrls,
    linkedNodeTitles,
    showHistory,
    setShowHistory,
    history,
    loadHistory,
    externalChange,
    dismissExternalChange,
    refreshFromServer,
    icon,
    cover,
    handleIconChange,
    handleCoverChange,
    triggerAutoSave: debouncedAutoSave,
    flushAutoSave: debouncedAutoSave.flush,
    resetEditor,
    handleTitleChange,
    handleContentChange,
  };

  return <EditorContext.Provider value={value}>{props.children}</EditorContext.Provider>;
};

export function useEditor(): EditorContextValue {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error('useEditor must be used within an EditorProvider');
  }
  return context;
}
